import React, {useEffect, useState, useCallback, useContext, useRef} from 'react';
import {ticketsAction, ticketsActivityLog, ticketsTransition} from '../../../services/TicketsServices';
import {useParams} from 'react-router';
import {Box, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, Divider, TextField as MuiTextField} from '@mui/material';
import Typography from '@mui/material/Typography';
import {paymentAction} from '../../../services/PaymentServices';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import PrintIcon from '@mui/icons-material/Print';
import {DispatchContext} from "../../../store";
import {SNACKBAR_OPEN} from "../../../reducers";
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import TicketDetailsFields from './TicketDetailsFields';
import downloadTicketPdf from './ticketPdf';
import {useReactToPrint} from 'react-to-print';
import WristBandDesign from '../TicketDesignPrinting/WristBandDesign';

export default function TicketDetail() {
  const params = useParams();
  const dispatch = useContext(DispatchContext);
  const [ticket, setTicket] = useState();
  const [redirectUrl, setRedirectUrl] = useState();
  const [paymentId, setPaymentId] = useState(null);
  const [isPaymentClicked, setIsPaymentClicked] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundRemarks, setRefundRemarks] = useState('');
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const componentRef = useRef();
  const canRequestRefund = ticket?.state === 'completed' && !ticket?.refund_requested;
  const isPaymentSectionHidden = ['refunded', 'refund_rejected', 'expired', 'cancelled'].includes(ticket?.state);
  const canProceedPayment = ticket?.payment_type === 'electronic' && ticket?.state === 'awaiting_payment';
  const isTicketCompleted = ticket?.state === 'completed';

  const latestRefundNote = activityLogs
    ?.find((l) => ['request_refund', 'approve_refund', 'reject_refund'].includes(l?.transition))
    ?.description;

  const fetchTicket = useCallback(() => {
    ticketsAction('get', params?.id).then(res => {
      setTicket(res?.data);
      setRedirectUrl(res?.data?.payments?.[0]?.redirect_url);
      setPaymentId(res?.data?.payments?.[0]?.id);
    });
  }, [params?.id]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  useEffect(() => {
    if (!params?.id) return;
    ticketsActivityLog('get', params.id)
      .then((res) => {
        setActivityLogs(res?.data || []);
      })
      .catch(() => {
        setActivityLogs([]);
      });
  }, [params?.id]);

  const handlePay = () => {
    if (!paymentId) return;
    setIsPaymentClicked(true);
    paymentAction('put', paymentId, {action: 'start'})
      .then(() => {
        // Only redirect if payment start was successful
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      })
      .catch((error) => {
        console.error('Payment start failed:', error);
        // Don't redirect on failure, user can try again
      });
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleDownloadPdf = () => {
    downloadTicketPdf(ticket);
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const handleOpenPrintDialog = () => {
    setPrintDialogOpen(true);
  };

  const handleClosePrintDialog = () => {
    setPrintDialogOpen(false);
  };

  const handleSendEmail = () => {
    // TODO: Implement email sending functionality
    dispatch({
      type: SNACKBAR_OPEN,
      payload: {isNotify: true, severity: 'info', message: 'Email functionality coming soon!'}
    });
  };

  const handleOpenRefundDialog = () => {
    setRefundAmount(String(ticket?.payment_due || ''));
    setRefundRemarks('');
    setRefundDialogOpen(true);
  };

  const handleSubmitRefund = () => {
    const amount = Number(refundAmount);
    if (!refundRemarks?.trim()) return;
    if (!Number.isFinite(amount) || amount <= 0) return;

    ticketsTransition('put', params?.id, {
      action: 'request_refund',
      refund_amount: amount,
      remarks: refundRemarks,
    })
      .then(() => {
        dispatch({
          type: SNACKBAR_OPEN,
          payload: {isNotify: true, severity: 'success', message: 'Refund request submitted'}
        });
        setRefundDialogOpen(false);
        fetchTicket();
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || err?.response?.data?.message || 'Failed to submit refund request';
        dispatch({
          type: SNACKBAR_OPEN,
          payload: {isNotify: true, severity: 'error', message: msg}
        });
      });
  };

  return (
    <Box className={'justify'} sx={{mt: 4, px: { xs: 2, sm: 0 }}}>
      <Card sx={{p: { xs: 1.5, sm: 2 }, width: "100%", maxWidth: 900}}>
        <Box sx={{mb: 2}}>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 0 },
            }}
          >
            <Typography sx={{fontWeight: 700}}>Ticket Summary</Typography>
            {canRequestRefund && (
              <Button variant={'outlined'} size={'small'} onClick={handleOpenRefundDialog}
                      className={'text-capitalize'}>
                Request Refund
              </Button>
            )}
          </Box>
          <Box sx={{mt: 1, mb: 1}}>
            <Divider/>
          </Box>
          <TicketDetailsFields ticket={ticket} hidePaymentSections={isTicketCompleted} />
          {latestRefundNote && (
            <Box sx={{mt: 2}}>
              <Typography sx={{fontSize: 14}}>
                Refund Notes: <span style={{fontWeight: 700}}>{latestRefundNote}</span>
              </Typography>
            </Box>
          )}
          {!isPaymentSectionHidden && !isTicketCompleted && (
            <Box className={'justify'} sx={{p: 2, mt: 2, backgroundColor: '#f8f8f8'}}>
              <Typography sx={{fontSize: 15}}>
                Payable Amount: <span style={{fontWeight: 700}}>{ticket?.payment_due}</span>
              </Typography>
            </Box>
          )}

          {ticket?.payments?.[0]?.status === 'paid' &&
          <Box>
            <Button startIcon={<CloudDownloadOutlinedIcon/>} sx={{mt: 3}} onClick={handleDownloadPdf}
                    fullWidth variant={'contained'} className={'text-capitalize'}>Download PDF</Button>
          </Box>
          }

          {/* Show completed ticket message and actions */}
          {isTicketCompleted && (
            <Box sx={{mt: 3}}>
              <Box sx={{p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, mb: 2}}>
                <Typography sx={{fontSize: 14, color: '#2e7d32', fontWeight: 600}}>
                  Your ticket has been completed! You can now print your ticket or have it sent to your email.
                </Typography>
              </Box>
              <Box sx={{display: 'flex', gap: 2, flexDirection: {xs: 'column', sm: 'row'}}}>
                {/* <Button 
                  startIcon={<PrintIcon/>} 
                  onClick={handleSendEmail}
                  variant={'outlined'} 
                  className={'text-capitalize'}
                  fullWidth
                >
                  Print Receipt
                </Button> */}
                <Button 
                  startIcon={<PrintIcon/>} 
                  onClick={handleOpenPrintDialog}
                  variant={'contained'} 
                  className={'text-capitalize'}
                  fullWidth
                >
                  Print Ticket
                </Button>
              </Box>
            </Box>
          )}

          {!isPaymentSectionHidden && ticket?.payments?.[0]?.status !== 'paid' && !isTicketCompleted &&
          <>
            <Button sx={{mt: 3}} onClick={handlePay} disabled={!canProceedPayment || !redirectUrl || isPaymentClicked}
                    fullWidth variant={'contained'} className={'text-capitalize'}>Proceed Payment</Button>

            <Button startIcon={<RefreshIcon/>} sx={{mt: 2}} onClick={handleRefresh}
                    fullWidth variant={'outlined'} className={'text-capitalize'}>Refresh</Button>
          </>
          }

          {!isPaymentSectionHidden && isPaymentClicked && (
            <Button startIcon={<RefreshIcon/>} sx={{mt: 2}} onClick={handleRefresh}
                    fullWidth variant={'contained'} className={'text-capitalize'}>Check Status</Button>
          )}
        </Box>
      </Card>

      <Dialog open={refundDialogOpen} onClose={() => setRefundDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Request Refund</DialogTitle>
        <DialogContent>
          <Box sx={{mt: 1}}>
            <MuiTextField
              fullWidth
              size="small"
              label="Refund Amount"
              type="number"
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
            />
          </Box>
          <Box sx={{mt: 2}}>
            <MuiTextField
              fullWidth
              size="small"
              label="Refund Notes"
              value={refundRemarks}
              onChange={(e) => setRefundRemarks(e.target.value)}
              multiline
              minRows={3}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)} className={'text-capitalize'}>Cancel</Button>
          <Button
            variant={'contained'}
            onClick={handleSubmitRefund}
            disabled={!refundRemarks?.trim() || !refundAmount || Number(refundAmount) <= 0}
            className={'text-capitalize'}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Print Dialog */}
      <Dialog open={printDialogOpen} onClose={handleClosePrintDialog} fullWidth maxWidth="sm">
        <DialogTitle>Print Ticket</DialogTitle>
        <DialogContent>
          <Box sx={{mt: 1}}>
            <Typography sx={{fontSize: 14, mb: 2}}>
              Print your ticket for museum entry
            </Typography>
            <Box sx={{display: 'none'}}>
              <WristBandDesign ref={componentRef} ticket={ticket}/>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePrintDialog} className={'text-capitalize'}>Cancel</Button>
          <Button
            variant={'contained'}
            onClick={handlePrint}
            className={'text-capitalize'}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
